import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import {
  Building2,
  CheckCircle2,
  FolderKanban,
  GraduationCap,
  Handshake,
  LayoutDashboard,
  Newspaper,
  RefreshCw,
  UserCog,
} from "lucide-react";

export type AdminSiteStatisticsPayload = {
  branches_total: number;
  branches_with_details: number;
  branches_with_portal_admin: number;
  batches_total: number;
  /** Distinct portal users with at least one batch-scoped grant (batch_profile / news / projects). */
  batch_portal_users_total: number;
  batches_with_details: number;
  batches_with_portal_admin: number;
  associations_total: number;
  associations_with_details: number;
  associations_with_portal_admin: number;
  news_total: number;
  news_published: number;
  projects_total: number;
  portal_assignment_rows: number;
};

function pct(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.min(100, Math.round((part / whole) * 100));
}

function MicroBar({ value, className }: { value: number; className: string }) {
  return (
    <div className="h-1 overflow-hidden rounded-full bg-brand-blue/[0.08]">
      <div className={`h-full rounded-full ${className}`} style={{ width: `${value}%` }} />
    </div>
  );
}

type EntityTheme = {
  gradient: string;
  iconWrap: string;
  icon: typeof Building2;
  barPrimary: string;
  barSecondary: string;
};

const ENTITY_THEMES: Record<"branches" | "batches" | "associations", EntityTheme> = {
  branches: {
    gradient: "from-emerald-600/90 via-teal-600/85 to-brand-blue/90",
    iconWrap: "bg-emerald-50 text-emerald-700 ring-emerald-600/12",
    icon: Building2,
    barPrimary: "bg-emerald-500",
    barSecondary: "bg-teal-500",
  },
  batches: {
    gradient: "from-amber-600/90 via-orange-600/80 to-brand-maroon/85",
    iconWrap: "bg-amber-50 text-amber-800 ring-amber-600/12",
    icon: GraduationCap,
    barPrimary: "bg-amber-500",
    barSecondary: "bg-orange-500",
  },
  associations: {
    gradient: "from-violet-600/88 via-indigo-600/85 to-brand-blue/92",
    iconWrap: "bg-violet-50 text-violet-700 ring-violet-600/12",
    icon: Handshake,
    barPrimary: "bg-violet-500",
    barSecondary: "bg-indigo-500",
  },
};

function EntityCompactCard({
  kind,
  title,
  total,
  headline,
  headlineHint,
  withDetails,
  withAdmin,
}: {
  kind: keyof typeof ENTITY_THEMES;
  title: string;
  /** Denominator for Profile / Admins progress bars (entity rows). */
  total: number;
  /** Optional headline number when it differs from `total` (e.g. batch portal users vs batch rows). */
  headline?: number;
  headlineHint?: string;
  withDetails: number;
  withAdmin: number;
}) {
  const theme = ENTITY_THEMES[kind];
  const Icon = theme.icon;
  const headlineNum = headline ?? total;
  const pContent = pct(withDetails, total);
  const pAdmin = pct(withAdmin, total);
  return (
    <article className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-brand-blue/[0.06]">
      <div className={`h-0.5 bg-gradient-to-r ${theme.gradient}`} aria-hidden />
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <div
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ${theme.iconWrap}`}
        >
          <Icon className="h-4 w-4" strokeWidth={2} aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <div className="min-w-0">
              <h3 className="truncate font-display text-sm font-bold text-brand-blue">{title}</h3>
              {headlineHint ? (
                <p className="truncate text-[9px] leading-tight text-brand-blue/50">{headlineHint}</p>
              ) : null}
            </div>
            <span className="shrink-0 font-display text-base font-bold tabular-nums text-brand-blue">
              {headlineNum.toLocaleString()}
            </span>
          </div>
          <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1">
            <div>
              <div className="mb-0.5 flex justify-between text-[10px] text-brand-blue/60">
                <span>Profile</span>
                <span className="tabular-nums text-brand-blue/80">
                  {withDetails}/{total} ({pContent}%)
                </span>
              </div>
              <MicroBar value={pContent} className={theme.barPrimary} />
            </div>
            <div>
              <div className="mb-0.5 flex justify-between text-[10px] text-brand-blue/60">
                <span>Admins</span>
                <span className="tabular-nums text-brand-blue/80">
                  {withAdmin}/{total} ({pAdmin}%)
                </span>
              </div>
              <MicroBar value={pAdmin} className={theme.barSecondary} />
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

export function AdminSiteStatistics() {
  const [stats, setStats] = useState<AdminSiteStatisticsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const statsRef = useRef(stats);
  statsRef.current = stats;

  const load = useCallback(async () => {
    setError(null);
    if (statsRef.current) setRefreshing(true);
    else setLoading(true);
    const { data, error: rpcError } = await supabase.rpc("admin_site_statistics");
    if (rpcError) {
      setError(rpcError.message);
      setStats(null);
      setLoading(false);
      setRefreshing(false);
      return;
    }
    if (data && typeof data === "object") {
      setStats(data as AdminSiteStatisticsPayload);
    } else {
      setError("Unexpected response from server.");
      setStats(null);
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const drafts = stats ? Math.max(0, stats.news_total - stats.news_published) : 0;

  return (
    <section
      className="mb-6 overflow-hidden rounded-xl border border-brand-blue/[0.08] bg-white shadow-sm"
      aria-labelledby="admin-site-stats-heading"
    >
      <div className="relative bg-gradient-to-r from-brand-blue to-[#062a52] px-4 py-3">
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(ellipse 70% 80% at 100% 0%, rgba(130,0,0,0.35), transparent 55%)",
          }}
        />
        <div className="relative flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <LayoutDashboard className="h-4 w-4 shrink-0 text-white/90" aria-hidden />
            <div className="min-w-0">
              <h2
                id="admin-site-stats-heading"
                className="font-display text-base font-bold leading-tight text-white sm:text-lg"
              >
                Site overview
              </h2>
              <p className="hidden text-[11px] text-white/65 sm:block">
                Totals for all admins · bars = share of records in each group
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading || refreshing}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-white/12 px-2.5 py-1.5 text-xs font-semibold text-white ring-1 ring-white/20 hover:bg-white/18 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} aria-hidden />
            Refresh
          </button>
        </div>
      </div>

      {loading && !stats ? (
        <div className="animate-pulse space-y-2 p-3">
          <div className="h-12 rounded-lg bg-brand-gray/80" />
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="h-16 rounded-lg bg-brand-gray/60" />
            <div className="h-16 rounded-lg bg-brand-gray/60" />
            <div className="h-16 rounded-lg bg-brand-gray/60" />
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="border-t border-red-100 bg-red-50/90 px-4 py-3 text-xs text-red-950">
          <p className="font-medium">{error}</p>
          <p className="mt-1 text-[11px] text-red-800/90">
            Run{" "}
            <code className="rounded bg-red-100/80 px-1 font-mono">migrate-admin-site-statistics.sql</code>{" "}
            in Supabase if needed.
          </p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-2 text-[11px] font-bold uppercase tracking-wide text-brand-maroon underline"
          >
            Try again
          </button>
        </div>
      ) : null}

      {stats ? (
        <div
          className={`border-t border-brand-blue/[0.06] bg-brand-gray/25 p-3 transition-opacity duration-200 ${refreshing ? "opacity-55" : ""}`}
        >
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="flex items-center gap-2 rounded-lg bg-white px-2.5 py-2 ring-1 ring-brand-blue/[0.05]">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-brand-blue/55">
                  Published
                </p>
                <p className="font-display text-lg font-bold tabular-nums leading-none text-brand-blue">
                  {stats.news_published}
                </p>
                <p className="text-[10px] text-brand-blue/50">{drafts} drafts</p>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-white px-2.5 py-2 ring-1 ring-brand-blue/[0.05]">
              <Newspaper className="h-4 w-4 shrink-0 text-sky-600" aria-hidden />
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-brand-blue/55">
                  News total
                </p>
                <p className="font-display text-lg font-bold tabular-nums leading-none text-brand-blue">
                  {stats.news_total}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-white px-2.5 py-2 ring-1 ring-brand-blue/[0.05]">
              <FolderKanban className="h-4 w-4 shrink-0 text-amber-600" aria-hidden />
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-brand-blue/55">
                  Projects
                </p>
                <p className="font-display text-lg font-bold tabular-nums leading-none text-brand-blue">
                  {stats.projects_total}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-white px-2.5 py-2 ring-1 ring-brand-blue/[0.05]">
              <UserCog className="h-4 w-4 shrink-0 text-brand-maroon" aria-hidden />
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-brand-blue/55">
                  Portal grants
                </p>
                <p className="font-display text-lg font-bold tabular-nums leading-none text-brand-blue">
                  {stats.portal_assignment_rows}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            <EntityCompactCard
              kind="branches"
              title="Branches"
              total={stats.branches_total}
              withDetails={stats.branches_with_details}
              withAdmin={stats.branches_with_portal_admin}
            />
            <EntityCompactCard
              kind="batches"
              title="Batch registrations"
              headline={stats.batch_portal_users_total}
              headlineHint="Users with portal access to a batch"
              total={stats.batches_total}
              withDetails={stats.batches_with_details}
              withAdmin={stats.batches_with_portal_admin}
            />
            <EntityCompactCard
              kind="associations"
              title="Associations"
              total={stats.associations_total}
              withDetails={stats.associations_with_details}
              withAdmin={stats.associations_with_portal_admin}
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}
